"use client";
// LEGACY UI: replace with Popover from @workspace/frontend-ui.
// Don't import this in new code; legacy-ui.json tracks the remaining importers
// and only shrinks (scripts/legacy-ui-check.mjs).

import classNames from "@casino/external/classNames";
import { createContext, useContext, useEffect, useRef, useState } from "react";

type Position =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

type PopoverContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  position: Position;
};

const PopoverCtx = createContext<PopoverContextValue>({
  open: false,
  setOpen: () => {},
  position: "bottom",
});

const panelPositionClasses: Record<Position, string> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  "top-start": "bottom-full left-0 mb-2",
  "top-end": "bottom-full right-0 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  "bottom-start": "top-full left-0 mt-2",
  "bottom-end": "top-full right-0 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  "left-start": "right-full top-0 mr-2",
  "left-end": "right-full bottom-0 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
  "right-start": "left-full top-0 ml-2",
  "right-end": "left-full bottom-0 ml-2",
};

type RootProps = {
  children:
    | React.ReactNode
    | (({ open }: { open: boolean }) => React.ReactNode);
  position?: Position;
  className?: string;
};

const PopoverRoot = ({
  children,
  position = "bottom",
  className,
}: RootProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <PopoverCtx.Provider value={{ open, setOpen, position }}>
      <div ref={containerRef} className={classNames("relative", className)}>
        {typeof children === "function" ? children({ open }) : children}
      </div>
    </PopoverCtx.Provider>
  );
};

type TriggerProps = { children: React.ReactNode; className?: string };

const Trigger = ({ children, className }: TriggerProps) => {
  const { setOpen, open } = useContext(PopoverCtx);
  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames("cursor-pointer", className)}
      onClick={() => setOpen(!open)}
      onKeyDown={(e) => e.key === "Enter" && setOpen(!open)}
    >
      {children}
    </div>
  );
};

type PanelProps = {
  children:
    | React.ReactNode
    | (({
        open,
        close,
      }: {
        open: boolean;
        close: () => void;
      }) => React.ReactNode);
  className?: string;
  isStatic?: boolean;
};

const Panel = ({ children, className, isStatic }: PanelProps) => {
  const { open, setOpen, position } = useContext(PopoverCtx);

  if (!open && !isStatic) return null;

  const close = () => setOpen(false);

  return (
    <div
      className={classNames(
        "absolute z-[999999] w-72 rounded-xl box-border bg-background shadow-lg overflow-y-auto focus:outline-none",
        panelPositionClasses[position],
        className,
      )}
    >
      {typeof children === "function" ? children({ open, close }) : children}
    </div>
  );
};

type GroupProps = { children: React.ReactNode; className?: string };

const Group = ({ children, className }: GroupProps) => (
  <div className={className}>{children}</div>
);

type ArrowProps = { className?: string };

const Arrow = ({ className }: ArrowProps) => (
  <div
    className={classNames(
      "block relative rotate-45 w-3 h-3 rounded-sm bg-background",
      className,
    )}
  />
);

const Popover = Object.assign(PopoverRoot, { Trigger, Panel, Group, Arrow });

export default Popover;
